/**
 * Thumbnails, made in the browser from the file the user just picked.
 *
 * The server has no image library and should not grow one to shrink a photo:
 * the uploader's browser already holds the file, decodes images natively and
 * can pull a frame out of a video without downloading anything. The result is
 * a small JPEG data URL stored beside the attachment, which is what lets every
 * desk's grid paint pictures without a click (QA report §5.2).
 *
 * Every function here resolves to null rather than throwing: a file the
 * browser cannot picture simply gets its type badge in the grid.
 */

import { THUMBNAIL_EDGE, fitWithin, kindOf } from '../../domain/attachments';

/** The server's cap on a thumbnail data URL (lib/attachments.js MAX_THUMBNAIL_CHARS). */
const MAX_CHARS = 200 * 1024;

/** Paint a drawable onto a white canvas at thumbnail size and encode it small enough. */
function encode(drawable, sourceWidth, sourceHeight) {
  const { width, height } = fitWithin(sourceWidth, sourceHeight, THUMBNAIL_EDGE);
  if (!width || !height) return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // White under a transparent PNG, or the JPEG turns its background black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(drawable, 0, 0, width, height);
  for (const quality of [0.78, 0.6, 0.45]) {
    const url = canvas.toDataURL('image/jpeg', quality);
    if (url.length <= MAX_CHARS) return url;
  }
  return null;
}

async function imageThumbnail(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      const url = encode(bitmap, bitmap.width, bitmap.height);
      bitmap.close?.();
      return url;
    } catch {
      /* fall through to <img>, which decodes a few formats createImageBitmap refuses */
    }
  }
  const src = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = src;
    });
    return encode(img, img.naturalWidth, img.naturalHeight);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(src);
  }
}

function videoThumbnail(blob) {
  return new Promise((resolve) => {
    const src = URL.createObjectURL(blob);
    const video = document.createElement('video');
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(src);
      video.removeAttribute('src');
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), 8000);
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onloadeddata = () => {
      // A third of the way in, capped at a second: past a black lead-in frame.
      video.currentTime = Math.min(1, (Number(video.duration) || 3) / 3);
    };
    video.onseeked = () => {
      try {
        finish(encode(video, video.videoWidth, video.videoHeight));
      } catch {
        finish(null);
      }
    };
    video.onerror = () => finish(null);
    video.src = src;
  });
}

/**
 * A thumbnail data URL for this file, or null.
 * @param {Blob & { name?: string }} blob
 */
export async function makeThumbnail(blob) {
  if (!blob || typeof document === 'undefined') return null;
  const kind = kindOf(blob.type, blob.name);
  if (kind === 'image') return imageThumbnail(blob);
  if (kind === 'video') return videoThumbnail(blob);
  return null;
}

export default makeThumbnail;
